import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WebInterfaceSdk } from '@byted-sdk/account-api';
import { Button, Checkbox, Form, Input, message } from 'antd';
import { StoreValue } from 'antd/lib/form/interface';
import SceneLogo from '@/assets/images/SceneLogo.png';
import * as Apis from '@/apis/login';
import Utils from '@/utils';
import { useDispatch } from '@/store';
import { login } from '@/store/slices/user';
import { isDev, isPPE, isProd } from '@/config';
import Footer from './Footer';
import styles from './index.module.less';
import { ILoginReturn, IUserInfo } from '@/apis/login';
import TeaClient from '@/core/rtc/TeaClient';

const accountSDK = new WebInterfaceSdk({
  aid: 460549, // 应用aid
  appKey: 'abd6d2de946e82147702aedc5446e6ca', // 应用appKey，新业务接入必传，否则接口会被拦截
  scene: 'normal', // 行业帐号场景下传'aff', 默认'normal'
  //   host: 'demo.volcvideo.com', // 接口请求域名，接口域名与页面域名不一致时需使用
  isOversea: false,
  region: 'cn',
  scope: 'web',
});

enum ERROR_TYPES {
  VALID,
  EMPTY_STRING,
  INVALID_CHARACTERS,
}

const messages = {
  invalidVerificationCode: {
    1: '请填写验证码',
    2: '验证码输入有误，请重新输入',
  },
  invalidPhoneNumber: {
    1: '请填写手机号',
    2: '手机号输入有误，请重新输入',
  },
};

const PhoneValues = {
  cell_phone: '12345678905',
  code: '246810',
};

export default function () {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [time, setTime] = useState<number>(60);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const count = () => {
    let times = time;
    const timer = setInterval(function () {
      if (times < 1) {
        setTime(60);
        clearInterval(timer);
        setLoading(false);
      } else {
        times -= 1;
        setTime(times);
      }
    }, 1000);
  };

  const waitingForVerificationCode = () => {
    setLoading(true);
    count();
  }

  const requestVerificationCode: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // 验证电话号码格式正确性
    const formValue = await form.validateFields(['cell_phone']);

    if (isProd) {
      // prod 环境使用 accountSDK 请求验证码
      accountSDK.login
        .sendCode({
          mobile: `+86 ${formValue.cell_phone}`,
          extra_params: {
            is6Digits: 1,
          },
        })
        .then(() => {
          waitingForVerificationCode();
        })
        .catch((err) => {
          if (err.error_code === 7) {
            message.error('访问太频繁，请稍后再试');
            return;
          }
          message.error(`发送验证码失败 : ${(err && err.code) || '未知问题' }`);
          console.log('发送验证码失败 : ', err);
        });
    } else {
      // ppe 和 dev 环境使用火山业务服务器请求验证码
      Apis.sendLoginSms({
        cell_phone: formValue.cell_phone,
      });
      waitingForVerificationCode();
    }
  };

  /**
   * 验证码登陆
   * 1. 校验验证码
   * 2. 校验成功后，拿返回到 login_token 并存储
   * 3. 跳转到进房页面
   * @returns
   */
  const handleLogin = async () => {
    // 验证校验码格式正确性
    const formValue = await form.validateFields();

    const handlLoginRes = (loginRes: ILoginReturn) => {
      if (loginRes.code !== 200) {
        message.error(`业务服务器登录失败, 请重新登陆 : ${loginRes.message}`);
        console.log(`业务服务器登录失败, 请重新登陆 : ${loginRes.message}`);
        TeaClient.reportUserLogin(false, JSON.stringify(loginRes));
        return;
      }
      const userInfo: IUserInfo = loginRes.response;
      TeaClient.setLoginInfo(userInfo.user_name, userInfo.user_id, Utils.getDeviceId() || '');
      TeaClient.reportUserLogin(true);
      dispatch(login(userInfo));
      setTimeout(() => {
        navigate(`/${location.search}`);
      });
    }

    let loginRes = null;
    if (isProd) {
      // prod 环境使用 accountSDK 登录
      accountSDK.login
        .smsLogin({ mobile: `+86 ${formValue.cell_phone}`, code: formValue.code })
        .then(async () => {
          loginRes = await Apis.login();
          handlLoginRes(loginRes);
        })
        .catch((err) => {
          if (err.error_code === 1203) return message.error('错误次数过多或验证码过期，请稍后重试');
          if (err.error_code === 1202) return message.error('验证码错误');
          message.error(`业务服务器登录失败, 请重新登陆 : ${err.error_code}`);
        });
    } else {
      // ppe 和 dev 环境使用火山业务服务器登录
      loginRes = await Apis.verifyLoginSms({
        cell_phone: formValue.cell_phone,
        code: formValue.code,
      });
      handlLoginRes(loginRes);
    }
  };

  const validator = (
    value: StoreValue,
    errorTypeKey: 'invalidPhoneNumber' | 'invalidVerificationCode',
    isNotMatchRegEx: boolean
  ): Promise<void | Error> => {
    if (isDev || isPPE) {
      // 如果是 dev 或 ppe 环境, 无需验证
      return Promise.resolve();
    }

    let result: Promise<Error | void>;
    if (!value || isNotMatchRegEx) {
      const _value = value ? ERROR_TYPES.INVALID_CHARACTERS : ERROR_TYPES.EMPTY_STRING;
      result = Promise.reject(new Error(messages[errorTypeKey][_value]));
    } else {
      result = Promise.resolve();
    }
    return result;
  };

  return (
    <div className={styles.loginWrapper}>
      <div className={styles.mainWrapper}>
        <div className={styles.main}>
          <div className={styles.mainTitle}>
            <img width={400} src={SceneLogo} alt="logo" />
          </div>
          <Form form={form} onFinish={handleLogin} initialValues={isDev ? PhoneValues : {}}>
            <Form.Item
              name="cell_phone"
              validateTrigger="onChange"
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    const res = !/^[1][3,4,5,6,7,8,9][0-9]{9}$/.test(value);
                    return validator(value, 'invalidPhoneNumber', res);
                  },
                },
              ]}
            >
              <Input autoComplete="off" placeholder="输入手机号" className={styles['phone-input']} />
            </Form.Item>
            <Form.Item
              name="code"
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    const res = !/^\d{6}$/.test(value);
                    return validator(value, 'invalidVerificationCode', res);
                  },
                },
              ]}
            >
              <Input
                autoComplete="off"
                placeholder="输入验证码"
                suffix={
                  <button onClick={requestVerificationCode}>{loading ? `${time}s` : '获取验证码'}</button>
                }
              />
            </Form.Item>
            <Form.Item
              name="agree"
              valuePropName="checked"
              wrapperCol={{ span: 24 }}
              className={styles['login-agree']}
              rules={[
                {
                  required: true,
                  message: '请先阅读并同意',
                },
              ]}
            >
              <Checkbox>
                已阅读并同意
                <a href="https://www.volcengine.com/docs/6348/128955" target="_blank" rel="noreferrer">
                  《用户协议》
                </a>
                和
                <a href="https://www.volcengine.com/docs/6348/68918" target="_blank" rel="noreferrer">
                  《隐私权政策》
                </a>
              </Checkbox>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, curValues) => prevValues.agree !== curValues.agree}
            >
              {({ getFieldValue }) => {
                return (
                  <Form.Item>
                    <Button
                      disabled={!getFieldValue('agree')}
                      htmlType="submit"
                      type="primary"
                      className={styles['login-check']}
                    >
                      登录
                    </Button>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
